import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { GameStatus } from "@/types/api";

export interface GameSession {
  gameId: string;
  slug: string;
  gameTitle: string;
  playerToken: string;
  playerId: string;
  displayName: string;
  role: "owner" | "player";
  isModerator: boolean;
  joinedAt: number;
  gameStatus?: GameStatus;
}

interface GameStore {
  sessions: GameSession[];
  addSession: (session: GameSession) => void;
  updateSession: (
    gameId: string,
    updates: Partial<Omit<GameSession, "gameId">>,
  ) => void;
  removeSession: (gameId: string) => void;
  getSession: (gameId: string) => GameSession | undefined;
  clearSessions: () => void;
}

const STORE_VERSION = 3;

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      sessions: [],
      addSession: (session) =>
        set((state) => ({
          sessions: [
            ...state.sessions.filter((s) => s.gameId !== session.gameId),
            session,
          ],
        })),
      updateSession: (gameId, updates) =>
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.gameId === gameId ? { ...s, ...updates } : s,
          ),
        })),
      removeSession: (gameId) =>
        set((state) => ({
          sessions: state.sessions.filter((s) => s.gameId !== gameId),
        })),
      getSession: (gameId) =>
        get().sessions.find((s) => s.gameId === gameId),
      clearSessions: () => set({ sessions: [] }),
    }),
    {
      name: "ispyfr-sessions",
      version: STORE_VERSION,
      partialize: (state) => ({ sessions: state.sessions }),
      migrate: () => {
        // Clear old v1 sessions — incompatible shape
        return { sessions: [] };
      },
    },
  ),
);
